'use client';

import type { Spacing } from '@onefootprint/design-tokens';
import { primitives } from '@onefootprint/design-tokens';
import * as ScrollAreaRadix from '@radix-ui/react-scroll-area';
import { cx } from 'class-variance-authority';
import type * as CSS from 'csstype';
import { forwardRef, useEffect, useRef, useState } from 'react';

type ScrollAreaProps = {
  asChild?: boolean;
  children: React.ReactNode;
  className?: string;
  hideBottomLine?: boolean;
  hideTopLine?: boolean;
  padding?: Spacing;
  paddingTop?: Spacing;
  paddingRight?: Spacing;
  paddingBottom?: Spacing;
  paddingLeft?: Spacing;
  height?: CSS.Property.Height;
  maxHeight?: CSS.Property.MaxHeight;
  overflow?: CSS.Property.Overflow;
};

const SPACING = primitives.spacing;

const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    {
      children,
      className,
      asChild,
      hideBottomLine,
      hideTopLine,
      padding,
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,
      height,
      maxHeight = '100%',
    },
    ref,
  ) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const [isOverflowTop, setIsOverflowTop] = useState(false);
    const [isOverflowBottom, setIsOverflowBottom] = useState(false);

    useEffect(() => {
      const scrollContainer = viewportRef.current;

      const checkOverflow = () => {
        if (scrollContainer) {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
          setIsOverflowTop(scrollTop > 0);
          setIsOverflowBottom(scrollTop + clientHeight < scrollHeight);
        }
      };

      checkOverflow();

      scrollContainer?.addEventListener('scroll', checkOverflow);

      return () => {
        scrollContainer?.removeEventListener('scroll', checkOverflow);
      };
    }, []);

    return (
      <ScrollAreaRadix.Root
        className={cx('fp-scroll-area flex flex-col box-border min-h-full relative', className)}
        style={
          {
            maxHeight,
            height,
            '--fp-scroll-p': padding && SPACING[padding],
            '--fp-scroll-pt': paddingTop && SPACING[paddingTop],
            '--fp-scroll-pr': paddingRight && SPACING[paddingRight],
            '--fp-scroll-pb': paddingBottom && SPACING[paddingBottom],
            '--fp-scroll-pl': paddingLeft && SPACING[paddingLeft],
          } as React.CSSProperties
        }
        ref={ref}
      >
        <ScrollAreaRadix.Viewport
          ref={viewportRef}
          className="fp-scroll-viewport w-full box-border max-h-full min-h-full"
          data-overflow-top={isOverflowTop && !hideTopLine}
          data-overflow-bottom={isOverflowBottom && !hideBottomLine}
          asChild={asChild}
        >
          {children}
        </ScrollAreaRadix.Viewport>
        <ScrollAreaRadix.Scrollbar orientation="vertical">
          <ScrollAreaRadix.Thumb />
        </ScrollAreaRadix.Scrollbar>
      </ScrollAreaRadix.Root>
    );
  },
);

export default ScrollArea;
